import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faTrash, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import CampaignFeature from "../../Campaigns/CampaignFeature/CampaignFeature";
import QRCodeModal from "../QRCodeModal/QRCodeModal";

const projectOptions = [
  { value: "planning", label: "Planning" },
  { value: "construction", label: "Under Construction" },
  { value: "completed", label: "Completed" },
];

const inputClass = "w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-blue";
const selectClass = "w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-blue appearance-none bg-white cursor-pointer pr-8";
const labelClass = "block !text-sm !font-semibold text-gray-900 mb-1 text-left";
const errorClass = "text-red-500 !text-xs mt-1";

const Field = ({ label, required, error, children }) => (
  <div className="flex flex-col">
    <label className={labelClass}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className={errorClass}>{error}</p>}
  </div>
);

const CapitalCampaignForm = ({
  capitalFormValues,
  setCapitalFormValues,
  capitalFormErrors,
  setCapitalFormErrors,
  handleCampaignSubmit,
  handleImageChange,
  imagePreviews,
  campaignType,
  isLoading,
  qrModalOpen,
  campaignUrl,
  setQrModalOpen,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const finalValue =
      name === "project_status"
        ? projectOptions.find((o) => o.value === value)?.value || value
        : value;
    if (name === "donation_goal" && parseFloat(value) < 0) return;
    setCapitalFormValues((prev) => ({ ...prev, [name]: finalValue }));
    setCapitalFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const syntheticEvent = { target: { files: e.dataTransfer.files, name: "images" } };
    handleImageChange(syntheticEvent, setCapitalFormErrors, setCapitalFormValues);
  };

  const removeImage = (index) => {
    setCapitalFormValues((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  return (
    <div>
      <h2 className="!font-bold text-orange !text-2xl text-center mb-6">
        Create New Capital Campaign
      </h2>

      <form onSubmit={handleCampaignSubmit} className="flex flex-col gap-4">
        {/* Campaign Title */}
        <Field label="Campaign Title" required error={capitalFormErrors.campaign_title}>
          <input
            type="text"
            name="campaign_title"
            placeholder="E.g. Riley Needs Your Help"
            value={capitalFormValues.campaign_title}
            onChange={handleInputChange}
            className={inputClass}
          />
        </Field>

        {/* Description */}
        <Field label="Description" required error={capitalFormErrors.description}>
          <textarea
            name="description"
            placeholder="Write a description..."
            rows={5}
            value={typeof capitalFormValues.description === "string" ? capitalFormValues.description : ""}
            onChange={(e) => {
              setCapitalFormValues((prev) => ({ ...prev, description: e.target.value }));
              setCapitalFormErrors((prev) => ({ ...prev, description: "" }));
            }}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-blue resize-none"
          />
        </Field>

        {/* Land Area + Land Location */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Land Area" required error={capitalFormErrors.land_area}>
            <input
              type="text"
              name="land_area"
              placeholder="E.g. Land for 50 animals"
              value={capitalFormValues.land_area}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
          <Field label="Land Location" required error={capitalFormErrors.land_location}>
            <input
              type="text"
              name="land_location"
              placeholder="E.g. Los Angeles, CA"
              value={capitalFormValues.land_location}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
        </div>

        {/* Project Status + Estimated Completion Date */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Project Status" required error={capitalFormErrors.project_status}>
            <div className="relative">
              <select
                name="project_status"
                value={capitalFormValues.project_status}
                onChange={handleInputChange}
                className={selectClass}
              >
                <option value="" disabled>Select Status</option>
                {projectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </Field>
          <Field label="Estimated Completion Date" required error={capitalFormErrors.estimated_completion_date}>
            <input
              type="date"
              name="estimated_completion_date"
              value={capitalFormValues.estimated_completion_date}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
        </div>

        {/* Donation Goal + Donation Box Title */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Donation Goal" required error={capitalFormErrors.donation_goal}>
            <input
              type="number"
              name="donation_goal"
              placeholder="E.g. 10"
              value={capitalFormValues.donation_goal}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
          <Field
            label={<>Donation Box Title <span className="text-gray-400 font-normal">(max. 30 chars)</span></>}
            required
            error={capitalFormErrors.donation_box_title}
          >
            <input
              type="text"
              name="donation_box_title"
              placeholder="E.g. Donate"
              value={capitalFormValues.donation_box_title}
              onChange={handleInputChange}
              maxLength={30}
              className={inputClass}
            />
          </Field>
        </div>

        {/* Video URLs */}
        <Field label={<>Video URLs <span className="text-gray-400 font-normal">(separated by commas)</span></>}>
          <input
            type="text"
            name="video_urls"
            placeholder="E.g. video1.mov, video2.mov, video3.mov"
            value={capitalFormValues.video_urls}
            onChange={handleInputChange}
            className={inputClass}
          />
        </Field>

        {/* Upload Images */}
        <div>
          <label className={labelClass}>
            Upload Images/Graphics <span className="text-gray-400 font-normal">(minimum of 4 images)</span>
            <span className="text-red-500 ml-0.5">*</span>
          </label>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${dragOver ? "border-blue bg-blue/5" : "border-gray-300 bg-gray-50"}`}
          >
            <FontAwesomeIcon icon={faUpload} className="text-blue text-2xl mb-3" />
            <p className="text-sm text-gray-700">
              Drag & drop files or{" "}
              <span className="text-blue underline">choose file</span> to upload
            </p>
            <p className="text-xs text-gray-400 mt-1">Supported formates: JPEG, PNG, PSD</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            name="images"
            multiple
            hidden
            onChange={(e) => handleImageChange(e, setCapitalFormErrors, setCapitalFormValues)}
          />

          {capitalFormValues.images?.length > 0 && (
            <div className="flex flex-col gap-2 mt-3">
              {capitalFormValues.images.map((img, i) => (
                <div key={i} className="flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2">
                  <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <p className="!text-sm text-gray-800 font-medium">{img.file?.name || `Image ${i + 1}`}</p>
                    <p className="!text-xs text-green-500 flex items-center gap-1">
                      Uploaded <FontAwesomeIcon icon={faCircleCheck} />
                    </p>
                  </div>
                  <button type="button" onClick={() => removeImage(i)} className="text-red-400 hover:text-red-600">
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {capitalFormErrors.images && (
            <p className={errorClass}>{capitalFormErrors.images}</p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setPreviewModalOpen(true); }}
            className="flex-1 bg-blue text-white font-bold py-3 rounded-full hover:opacity-90"
          >
            Preview Campaign
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-blue-navy text-white font-bold py-3 rounded-full hover:opacity-90 disabled:opacity-50"
          >
            {isLoading ? "Creating..." : "Create Campaign"}
          </button>
        </div>
      </form>

      {previewModalOpen && (
        <CampaignFeature
          isOpen={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          campaignData={capitalFormValues}
          imagePreviews={imagePreviews}
          graphicImagePreviews={[]}
          campaignType={campaignType}
        />
      )}

      <QRCodeModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        campaignUrl={campaignUrl}
      />
    </div>
  );
};

export default CapitalCampaignForm;
